import lodash from 'lodash'
import router from '@/router'
import camundaAPI from '@/services/camunda'
import componentAPI from '@/services/component'
import variablesProcessing from '@/utils/variablesProcessing'
import { getLocale, mergeProcessLocale } from '@/i18n'
import minioAPI from '@/services/minio'

const state = {
  filters: [],
  filter: null,
  tasks: [],
  news: [],
  newsTotal: null,
  task: null,
  variables: null,
  historyTasks: null,
  view: null,
  style: null,
  total: null,
  sort: {
    sorting: [
      { sortBy: 'created', sortOrder: 'desc' },
    ],
  },
  page: {
    current: 1,
    per: 10,
  },
  newsSort: {
    sorting: [
      { sortBy: 'processVariable', sortOrder: 'desc', "parameters":{
        "variable" : "_businessKey",
        "type" : "String"
      } },
    ],
  },
  newsPage: {
    current: 1,
    per: 10,
  },
  submitMode: false,
}

const getters = {
  /* eslint-disable no-shadow */
  filters: state => state.filters,
  filter: state => state.filter,
  tasks: state => state.tasks,
  news: state => state.news,
  newsTotal: state => state.newsTotal,
  newsPage: state => state.newsPage,  
  task: state => state.task,
  total: state => state.total,
  page: state => state.page,
  variables: state => state.variables,
  view: state => state.view,
  style: state => state.style,
  submitMode: state => state.submitMode,
  variable: state => state.variable,
  historyTasks: state => state.historyTasks,
}

const mutations = {
  set(state, { type, value }) {
    state[type] = value
  },
}

const actions = {
  getFilters({ commit, rootGetters }) {
    
    const isCamundaAdmin = lodash.some(rootGetters['auth/groups'], group => group.id == 'camunda-admin')
    const isBughalter = lodash.some(rootGetters['auth/groups'], group => group.id.endsWith('_buhgalter'))

    return camundaAPI.task.filters().then((response) => {
      const newFilters = lodash.filter(response.data, function(f){        
        return f.name !== 'Active news' && (isCamundaAdmin || (f.name !== 'Все задачи' && (f.name !== 'Отложенные' || isBughalter)))
      })
      const sortedFilters = lodash.orderBy(newFilters, 'properties.priority', 'asc')
      commit('set', { type: 'filters', value: sortedFilters })
    })
  },

  selectFilter({ commit, state }, payload) {
    const page = { current: 1, per: state.page.per }
    commit('set', { type: 'page', value: page })
    commit('set', { type: 'filter', value: payload })
  },

  paginateTasks({ commit, state, dispatch }, payload) {
    const page = { current: payload, per: state.page.per }
    commit('set', { type: 'page', value: page })
    dispatch('getTasks')
  },

  getTasks({ commit, state }) {
    camundaAPI.task.list(state.filter.id, state.page, state.sort).then((response) => {
      commit('set', { type: 'tasks', value: response.data._embedded })
      commit('set', { type: 'total', value: response.data.count })
      state.filter.itemCount = response.data.count
    })
  },

  paginateNews({ commit, state, dispatch }, payload) {
    const page = { current: payload, per: state.newsPage.per }
    commit('set', { type: 'newsPage', value: page })
    dispatch('getNews')
  },

  getNews({ commit, state }) {
    commit('set', { type: 'news', value: [] })
    camundaAPI.task.news(state.newsPage, state.newsSort).then((response) => {
      let length = response.data._embedded.task.length;
      let count = 0;
      response.data._embedded.task.forEach((item) => {          
        item.thumbnailUrl = undefined;

        let photos = undefined;
        item._embedded.variable.forEach(element => {
          if(element.name === 'photo'){
            photos = element.value;
          }
        });
        
        if(photos){
            minioAPI.presignedGetObjectUrls([JSON.parse(photos)[0]]).then((result) => {
              item.thumbnailUrl = result.data[0].thumbnailUrl;
              count = count + 1;
              if(length === count){
                commit('set', { type: 'news', value: response.data._embedded.task })
              }
            })
        } else {
          item.thumbnailUrl = '/static/no_image.jpeg';
          count = count + 1;
          if(length === count){
            commit('set', { type: 'news', value: response.data._embedded.task })
          }
        }        
      });      
      commit('set', { type: 'newsTotal', value: response.data.count })
    })
  },

  getTask({ commit, state, dispatch }, payload) {
    commit('set', { type: 'task', value: null })
    commit('set', { type: 'variables', value: null })
    commit('set', { type: 'view', value: null })
    commit('set', { type: 'style', value: null })
    return camundaAPI.task.get(payload).then((response) => {      
      commit('set', { type: 'task', value: response.data })
      return dispatch('getVariables', state.task.id)
    })
  },

  updateTask({ commit }, payload) {
    camundaAPI.task.get(payload).then((response) => {
      commit('set', { type: 'task', value: response.data })
    })
  },

  getVariables({ commit, state, dispatch }, payload) {
    return camundaAPI.task.variables.get(payload).then((response) => {
      const savedVariables = variablesProcessing.parse(localStorage.getItem(`task-vars-${state.task.id}`), true)
      const taskVariables = variablesProcessing.parse(response.data, false)
      lodash.merge(taskVariables, savedVariables)
      commit('set', { type: 'variables', value: taskVariables })
      return dispatch('getHistoryTasks', `${state.task.processInstanceId}`)
    })
  },

  getHistoryTasks({ commit, state, dispatch }, payload) {
    return camundaAPI.task.history(payload).then((response) => {
      const historyTasks = response.data;
      commit('set', { type: 'historyTasks', value: historyTasks })
      return dispatch('getView', `${state.task.taskDefinitionKey}`)
    })
  },

  updateVariables({ state }, payload) {
    return camundaAPI.task.variables.update(state.task.id, { modifications: variablesProcessing.stringify(payload, false) })
  },

  /* eslint-disable consistent-return */
  getView({ commit, state }, payload) {
    const taskFormKey = payload
    const processKey = state.task.processDefinitionId.split(':')[0]
    if (process.env.NODE_ENV === 'development') {
      import(`${process.env.PROCESS_PATH}/${getLocale()}.json`).then((obj) => {
        mergeProcessLocale(processKey, obj)
      })
      import(`${process.env.PROCESS_PATH}/${taskFormKey}.vue`).then((obj) => {
        commit('set', { type: 'view', value: obj.default })
      })
    } else {
      return componentAPI.load(processKey, taskFormKey).then((form) => {
        if (form.locale && form.locale.length > 0) {
          mergeProcessLocale(processKey, JSON.parse(form.locale))
        }
        commit('set', { type: 'view', value: form.obj })
        commit('set', { type: 'style', value: form.style })
      })
    }
  },

  back() {
    router.push('/task-list')
  },

  claim({ dispatch, state, rootGetters }) {
    camundaAPI.task.claim(state.task.id, rootGetters['auth/profile'].userName).then(() => {
      dispatch('updateTask', state.task.id)
      dispatch('getFilters')
    })
  },

  unclaim({ dispatch, state }) {
    camundaAPI.task.unclaim(state.task.id).then(() => {
      dispatch('updateTask', state.task.id)
      dispatch('getFilters')
    })
  },

  save({ state }, payload) {
    localStorage.setItem(`task-vars-${state.task.id}`, variablesProcessing.stringify(payload, true))
  },

  submit({ commit, dispatch }, payload) {
    commit('set', { type: 'submitMode', value: true })
    camundaAPI.task.submit(state.task.id, { variables: variablesProcessing.stringify(payload, false) }).then(() => {
      router.push('/task-list')
      dispatch('tools/setSnackbar', 'Задача успешна выполнена', { root: true })
      commit('set', { type: 'submitMode', value: false })
      dispatch('getFilters')
    })
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}



// WEBPACK FOOTER //
// ./src/store/modules/task.js